import { useTheme, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Box, Typography, Button } from '@mui/material'
import PropTypes from 'prop-types'

/**
 * @description Renders a confirmation modal dialog.
 *
 * @author Maria Eduarda Franke
 * @param {boolean} props.open - Determines whether the modal is open or not.
 * @param {function} props.onClose - The function to be called when the modal is closed.
 * @param {function} props.onConfirm - The function to be called when the confirm button is clicked.
 * @param {string} props.title - The title of the modal.
 * @param {string} props.id - The ID of the modal.
 * @param {string} props.description - The description of the modal.
 * @param {string} [props.cancelText='Cancelar'] - The text for the cancel button.
 * @param {string} [props.confirmText='Confirmar'] - The text for the confirm button.
 * @returns {JSX.Element} The rendered ConfirmModal component.
 */
const ConfirmModal = ({
    open, onClose, onConfirm,
    title, id, description,
    cancelText = 'Cancelar', confirmText = 'Confirmar', ...props }) => {

    const theme = useTheme()

    return (
        <Dialog
            open={open}
            onClose={onClose}
            fullWidth
            maxWidth='sm'
            {...props}
        >
            <DialogTitle>
                <Box display='flex' flexDirection='row' alignItems='baseline' gap={1}>
                    <Typography variant='h6'>{title}?</Typography>
                    {
                        id && (
                            <Typography variant='body1' color={theme.palette.text.secondary}>#{id}</Typography>
                        )
                    }
                </Box>
            </DialogTitle>
            <DialogContent>
                <DialogContentText>
                    {description}
                </DialogContentText>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>{cancelText}</Button>
                <Button onClick={onConfirm} autoFocus>{confirmText}</Button>
            </DialogActions>
        </Dialog>
    )
}

ConfirmModal.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    onConfirm: PropTypes.func.isRequired,
    title: PropTypes.string.isRequired,
    id: PropTypes.number,
    description: PropTypes.string.isRequired,
    cancelText: PropTypes.string,
    confirmText: PropTypes.string
}

export default ConfirmModal
